import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import CarouselEmbed from "./CarouselEmbed";
import ProductGrid from "./ProductGrid";
import InteractivePoll from "./InteractivePoll";
import { hydrateHtmlEmbeds } from "../lib/htmlEmbed";
import { htmlHasPoll, splitHtmlByPolls } from "../lib/pollEmbed";

// Matches `[[carousel:slug]]` or `[[products]]` shortcodes (case-insensitive).
const SHORTCODE_RE = /\[\[(carousel:[a-z0-9-]+|products)\]\]/gi;

/**
 * Splits an HTML body string by `[[carousel:slug]]` shortcodes and renders
 * each segment in turn — raw HTML through `dangerouslySetInnerHTML`, the
 * carousels through React. Lets admins drop a photo carousel anywhere in a
 * post or custom-page body without breaking the existing rich-text flow.
 *
 * `sanitize` is called on every HTML segment with the supplied
 * DOMPurify config + hooks already wired up by the parent module.
 */
export default function RichBodyWithCarousels({ html: htmlProp, sanitize, className, testId }) {
  // Collapse 2+ consecutive genuinely-empty `<p></p>` (TipTap's saved form
  // of a deliberate blank line — see the `.tiptap-prose p:empty` CSS rule)
  // into exactly one, so pressing Return several times in the editor for
  // "extra space" doesn't compound into an ever-taller gap once published —
  // any run, however long, renders as one single fixed-height spacer line.
  // Whitespace between the tags (however the editor happened to serialize
  // them) is tolerated so a run split across source lines still collapses.
  // Applied to the raw saved HTML before shortcode-splitting/sanitizing
  // below, same as the existing `stripEmptyParagraphs` pass.
  const html = collapseEmptyParagraphRuns(htmlProp);
  const rootRef = useRef(null);
  // Full-size tap-to-zoom lightbox for plain content images (photos
  // dropped into the body text). `null` = closed. Holds the tapped
  // image's `src` only — no need for a whole image list since there's
  // no prev/next here, just "here's the one you tapped, bigger".
  const [zoomSrc, setZoomSrc] = useState(null);

  // After every render pass, walk the newly-mounted DOM and hydrate any
  // `.html-embed-placeholder[data-html-b64]` divs the admin inserted
  // via the "Insert HTML embed" toolbar. `hydrateHtmlEmbeds` is
  // idempotent — it skips placeholders already tagged with
  // `data-hydrated="true"` so this doesn't double-mount an embed on
  // React re-renders. Also flags plain content images with a
  // zoom-in cursor so the tap-to-enlarge affordance is visible.
  useEffect(() => {
    if (!rootRef.current) return;
    hydrateHtmlEmbeds(rootRef.current);
    rootRef.current.querySelectorAll("img").forEach((img) => {
      if (img.closest("a, button, .html-embed-placeholder")) return;
      img.style.cursor = "zoom-in";
    });
  });

  // Tap/click-to-zoom for body images. Delegated on the root so it
  // works across re-renders without re-binding per <img> — the body is
  // raw `dangerouslySetInnerHTML` HTML, not React elements, so there's
  // nowhere to attach an onClick directly. Skips images that are
  // already interactive: `<a>` (admin made the image a clickable
  // link — let that navigate instead), `<button>` (PhotoCarousel
  // slides have their own full-size lightbox already), and anything
  // inside a third-party `.html-embed-placeholder` embed (don't
  // hijack clicks meant for that embed's own JS).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const onClick = (e) => {
      const img = e.target?.closest?.("img");
      if (!img || !root.contains(img)) return;
      if (img.closest("a, button, .html-embed-placeholder")) return;
      setZoomSrc(img.getAttribute("src"));
    };
    root.addEventListener("click", onClick);
    return () => root.removeEventListener("click", onClick);
  }, []);

  useEffect(() => {
    if (!zoomSrc) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setZoomSrc(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomSrc]);

  const lightbox = zoomSrc ? (
    <div
      className="lightbox fixed inset-0 z-[100] flex items-center justify-center bg-black/95 p-4"
      data-testid="post-body-image-lightbox"
      onClick={(e) => {
        if (e.target === e.currentTarget) setZoomSrc(null);
      }}
    >
      <button
        type="button"
        onClick={() => setZoomSrc(null)}
        data-testid="post-body-image-lightbox-close"
        aria-label="Close full-size image"
        className="absolute right-4 top-4 rounded-full border border-white/20 bg-white/10 p-2 text-white transition-colors hover:bg-white/20"
      >
        <X size={20} strokeWidth={2.5} />
      </button>
      <img
        src={zoomSrc}
        alt=""
        className="max-h-[90vh] max-w-[94vw] rounded-md object-contain"
        data-testid="post-body-image-lightbox-image"
      />
    </div>
  ) : null;

  if (!html) return lightbox;

  // Fast path: no carousel/product shortcodes AND no embedded poll →
  // render the body verbatim.
  const hasShortcode = SHORTCODE_RE.test(html);
  SHORTCODE_RE.lastIndex = 0;
  const hasPoll = htmlHasPoll(html);
  if (!hasShortcode && !hasPoll) {
    return (
      <>
        <div
          ref={rootRef}
          className={className}
          data-testid={testId}
          dangerouslySetInnerHTML={{ __html: sanitize(html) }}
        />
        {lightbox}
      </>
    );
  }

  const segments = [];
  let cursor = 0;
  for (const match of html.matchAll(SHORTCODE_RE)) {
    const start = match.index ?? 0;
    if (start > cursor) {
      segments.push({ type: "html", value: html.slice(cursor, start) });
    }
    const code = match[1].toLowerCase();
    if (code.startsWith("carousel:")) {
      segments.push({ type: "carousel", slug: code.slice("carousel:".length) });
    } else if (code === "products") {
      segments.push({ type: "products" });
    }
    cursor = start + match[0].length;
  }
  if (cursor < html.length) {
    segments.push({ type: "html", value: html.slice(cursor) });
  }

  // Strip wrapping `<p>` tags that TipTap wraps shortcodes in when they sit
  // on their own line. Otherwise we end up with an empty paragraph above
  // and below every carousel.
  const cleaned = stripEmptyParagraphs(segments);

  const renderHtmlWithPolls = (rawHtml) => splitHtmlByPolls(rawHtml);

  return (
    <>
      <div ref={rootRef} className={className} data-testid={testId}>
        {cleaned.map((seg, i) => {
          if (seg.type === "carousel") {
            return <CarouselEmbed key={`c-${i}`} slug={seg.slug} />;
          }
          if (seg.type === "products") {
            return <ProductGrid key={`p-${i}`} />;
          }
          const subsegs = renderHtmlWithPolls(seg.value);
          return (
            <div key={`h-${i}`}>
              {subsegs.map((sub, j) => {
                if (sub.type === "poll") {
                  return <InteractivePoll key={`poll-${i}-${j}`} pollId={sub.id} className="my-6 max-w-xl mx-auto" scrollableOptions />;
                }
                return (
                  <div
                    key={`raw-${i}-${j}`}
                    dangerouslySetInnerHTML={{ __html: sanitize(sub.value) }}
                  />
                );
              })}
            </div>
          );
        })}
      </div>
      {lightbox}
    </>
  );
}

function collapseEmptyParagraphRuns(html) {
  if (!html) return html;
  // `data-collapsed-empty-run` is just a traceability marker (visible in
  // devtools, matched by nothing in CSS) — the surviving `<p>` is still a
  // plain empty paragraph, styled by the existing `.tiptap-prose p:empty`
  // rule exactly like any single deliberate blank line.
  return html.replace(/(?:<p>\s*<\/p>\s*){2,}/gi, '<p data-collapsed-empty-run="true"></p>');
}

function stripEmptyParagraphs(segments) {
  // The shortcode-splitting above cuts `<p>[[shortcode]]</p>` right
  // through the middle: the html segment before the shortcode ends with
  // a dangling, unclosed `<p>`, and the segment after it starts with an
  // orphan `</p>`. Left alone, the browser's HTML parser silently
  // auto-closes that dangling `<p>` into a genuinely empty paragraph —
  // that's the "empty paragraph above/below every carousel" artifact
  // this function exists to prevent. Trim just those two structural
  // leftovers. Anything else — including a complete `<p></p>` the admin
  // deliberately authored for blank-line spacing, even right next to a
  // shortcode — is left completely untouched; see the
  // `.tiptap-prose p:empty::before` CSS rule in index.css, which is what
  // renders those as a visible gap once published.
  return segments
    .map((seg) =>
      seg.type === "html"
        ? { ...seg, value: seg.value.replace(/<p>\s*$/i, "").replace(/^\s*<\/p>/i, "") }
        : seg,
    )
    .filter((seg) => !(seg.type === "html" && seg.value.trim() === ""));
}
